<template>
  <div class="custom-pages-list">
    <HeadSection title="Custom Pages" permissionOfSection="custom-pages">
      <template #actions>
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            style="font-size: 16px; font-weight: 300; border-radius: 2px"
            @click="createCustomPage"
          >
            <a-icon type="plus" /> Create new
          </a-button>
        </a-col>
      </template>
    </HeadSection>

    <MainContainer :loading="userLoading" permissionOfSection="custom-pages">
      <div class="d-flex align-items-center mb-4">
        <AppSelect
          v-model="filters.applicationId"
          :apps="apps"
          :disabled="loading"
          class="mr-3"
          style="width: 220px"
          @change="listUpdate"
        />
        <LocalizationsSelect
          v-model="filters.localizationId"
          :localizations="languagesOptions"
          :disabled="loading"
          class="mr-3"
          @change="listUpdate"
        />
        <ActiveStatus
          v-model="filters.active"
          :disabled="loading"
          hide-title
          style="margin-top: auto"
          @change="listUpdate"
        />
        <div class="d-flex align-items-end justify-content-between ml-auto">
          <div>
            <span class="font-weight-bold">Search by</span>
            <a-select
              v-model="filters.selectedSearch"
              size="large"
              style="width: 140px"
            >
              <a-select-option
                v-for="option in searchOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.title }}
              </a-select-option>
            </a-select>
          </div>
          <a-input-search
            size="large"
            allow-clear
            v-model="filters.search"
            placeholder="Input search text"
            enter-button
            @change="debouncedListUpdate"
            :disabled="loading"
            class=""
          />
        </div>
      </div>
      <div
        class="content"
        :class="customPages.length <= 3 && !loading && 'force-grid'"
      >
        <div class="grid">
          <transition-group v-if="!loading" tag="div">
            <CustomPageCard
              v-for="customPage in customPages"
              :key="customPage.uuid"
              :info="customPage"
              :editPermission="editPermission"
              :languages="languages"
              @openFullInfo="onOpenInfoModal(customPage)"
              @openEdit="onOpenEditPage(customPage.uuid)"
              @changeActive="handleToggleActive"
            />
          </transition-group>
          <div v-else>
            <CustomPageCardSkeleton v-for="n in 10" :key="n" />
          </div>
          <EmptyState
            v-if="!customPages.length"
            :text="emptyStateText"
            :isAddDisabled="!createPermission"
            textBtn="Add"
            @add="createCustomPage"
          />
        </div>
      </div>
    </MainContainer>

    <CustomPageInfoModal
      v-model="isInfoModalVisible"
      :data="infoData"
      :hasEditPermission="editPermission"
      @close="isInfoModalVisible = false"
      @openEditPage="onOpenEditPage"
      @changeActive="handleToggleActive"
    />
  </div>
</template>
<script>
import CustomPageCardSkeleton from '@/views/apps/custom-pages/components/CustomPageCardSkeleton.vue'
import CustomPageCard from '@/views/apps/custom-pages/components/CustomPageCard.vue'
import CustomPageInfoModal from '@/views/apps/custom-pages/components/CustomPageInfoModal.vue'

import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import AppSelect from '@/components/custom/filters/appSelect.vue'
import LocalizationsSelect from '@/components/custom/filters/localizationsSelect.vue'
import ActiveStatus from '@/components/custom/filters/activeStatus.vue'
import EmptyState from '@/components/custom/emptyState.vue'

import debounce from 'lodash.debounce'
import { mapState, mapActions } from 'vuex'

import accessMix from '@/services/accessmix.js'
import apiClient from '@/services/axios'

export default {
  components: {
    HeadSection,
    MainContainer,
    AppSelect,
    LocalizationsSelect,
    EmptyState,
    ActiveStatus,
    CustomPageCardSkeleton,
    CustomPageCard,
    CustomPageInfoModal,
  },
  mixins: [accessMix],
  data() {
    return {
      pagesLoading: false,
      appsLoading: false,
      apps: undefined,
      searchOptions: [
        { title: 'UUID', value: 'uuid' },
        { title: 'Name', value: 'name' },
      ],
      filters: {
        applicationId: undefined,
        localizationId: undefined,
        active: true,
        selectedSearch: 'uuid',
        search: '',
      },
      pagination: {
        current: 1,
        results: 15,
        total: 0,
      },
      customPages: [],
      isInfoModalVisible: false,
      infoData: null,
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
      languages: (state) => state.langs.langData,
    }),
    loading() {
      return this.pagesLoading || this.appsLoading
    },
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    languagesOptions() {
      return this.languages.map(function (row) {
        return { value: row.id, label: row.desc }
      })
    },
    createPermission() {
      return this.checkPermissions(this.user.permissions, 'custom-pages', 'create')
    },
    editPermission() {
      return this.checkPermissions(
        this.user.permissions,
        'custom-pages',
        'edit',
      )
    },
    emptyStateText() {
      return this.filters.search?.length ? 'Oops! Nothing matches your search.' : 'Add first custom page'
    },
  },
  methods: {
    ...mapActions(['GET_LANGS']),
    onOpenInfoModal(data) {
      this.infoData = data
      this.isInfoModalVisible = true
    },
    onOpenEditPage(uuid) {
      if (uuid) {
        this.$router.push(`/custom-pages/constructor/${uuid}`)
      }
    },
    createCustomPage() {
      this.$router.push('/custom-pages/create')
    },
    async fetchApplications() {
      this.appsLoading = true
      try {
        const url = '/admin/applications'
        const response = await apiClient.get(url)
        if (response.data.data) {
          this.apps = response.data.data
        }
      } finally {
        this.appsLoading = false
      }
    },
    async fetchPages() {
      const { active, localizationId, applicationId, selectedSearch, search } =
        this.filters

      const searchParams = new URLSearchParams()

      searchParams.append('is_active', active)

      if (typeof localizationId !== 'undefined') {
        searchParams.append('lid', localizationId)
      }

      if (typeof applicationId !== 'undefined') {
        searchParams.append('app_id', applicationId)
      }

      if (search?.length) {
        selectedSearch === 'uuid'
          ? searchParams.append('uuid', search)
          : searchParams.append('name', search)
      }

      const response = await apiClient.get(`admin/custom-page?${searchParams}`)

      if (response.data.data) {
        this.customPages = JSON.parse(JSON.stringify(response.data.data ?? []))
      } else {
        this.customPages = []
      }
    },
    async listUpdate() {
      try {
        this.pagesLoading = true
        await this.fetchPages()
      } catch (e) {
        console.error(e)
      } finally {
        this.pagesLoading = false
      }
    },
    debouncedListUpdate: debounce(function () {
      this.listUpdate()
    }, 1000),
    async handleToggleActive(state, uuid) {
      const foundPage = this.customPages.find((p) => p.uuid === uuid)
      if (!foundPage) {
        return
      }

      const url = `admin/custom-page/chance-active/${uuid}`

      apiClient
        .post(url, {
          active: state,
        })
        .then((resp) => {
          if (resp?.status !== 200) {
            throw resp
          }

          this.$notification.success({
            message: 'Custom page active status changed',
          })
        })
        .catch((error) => {
          foundPage.is_active = !state

          this.$notification.error({
            message: 'Error while toggling active state',
            description: error.message,
          })
        })

      foundPage.is_active = state
    },
  },
  async created() {
    this.GET_LANGS()

    await this.fetchApplications()
    await this.listUpdate()
  },
}
</script>
<style lang="scss" scoped>
.content {
  &.force-grid .grid {
    & > div {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

.grid {
  & > div {
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  }
}

:deep .empty-container {
  display: block !important;
}
</style>
